import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { useMediaQuery } from "react-responsive";
import Cookies from "js-cookie";
import API_ENDPOINTS from "../config/api";

const ElectronicSignature = ({ onClose, onConfirm }) => {
  const navigate = useNavigate();
  const isDesktop = useMediaQuery({ minWidth: 768 });
  const [agreed, setAgreed] = useState(false);
  const [visible, setVisible] = useState(false);
  const [userName, setUserName] = useState("의뢰인");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [userData, setUserData] = useState(null);
  const [successFeePercentage, setSuccessFeePercentage] = useState(10);
  const [lawsuitAmount, setLawsuitAmount] = useState(5000);

  // 페이지 로드 시 모달이 하단에서 올라오도록 설정
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        // 성공보수 퍼센티지와 소송 금액 가져오기
        const feePercentage = Cookies.get("lawsuit_success_fee_percentage");
        if (feePercentage) {
          setSuccessFeePercentage(parseInt(feePercentage, 10));
        }

        const amount = Cookies.get("lawsuit_amount");
        if (amount) {
          setLawsuitAmount(parseInt(amount, 10));
        }

        const token = Cookies.get(
          API_ENDPOINTS.COOKIE_CONFIG.KEYS.ACCESS_TOKEN
        );

        if (!token) {
          throw new Error("인증 토큰이 없습니다. 다시 로그인해주세요.");
        }

        const response = await fetch(`${API_ENDPOINTS.BASE_URL}/clients/me`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        if (!response.ok) {
          throw new Error("사용자 정보를 가져오는데 실패했습니다.");
        }

        const data = await response.json();
        setUserData(data);
      } catch (err) {
        setError(err.message || "사용자 정보를 가져오는데 실패했습니다.");
      } finally {
        setVisible(true);
      }
    };

    fetchUserData();
  }, []);

  // 모달 닫기 처리
  const handleClose = () => {
    setVisible(false);
    setTimeout(() => {
      if (onClose) onClose();
    }, 300);
  };

  // 동의 처리
  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
  };

  const modalVariants = {
    hidden: {
      y: "100%",
      opacity: 1,
    },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        damping: 25,
        stiffness: 300,
      },
    },
    exit: {
      y: "100%",
      opacity: 1,
      transition: {
        duration: 0.3,
      },
    },
  };

  return (
    <ModalOverlay onClick={handleClose}>
      <AnimatePresence>
        {visible && (
          <ModalContainer
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={(e) => e.stopPropagation()}
          >
            <ModalHeader>
              <ModalTitle>쿠팡 소송 위임계약서</ModalTitle>
              <CloseButton onClick={handleClose}>&times;</CloseButton>
            </ModalHeader>
            <ModalBody>
              <AgreementTitle>
                <strong>소송 위임계약서</strong>
              </AgreementTitle>

              <AgreementParties>
                <div>사 건 명 : 쿠팡 개인정보 유출사태 손해배상청구</div>
                <div>위임인(갑): {userData?.name || ""}</div>
                <div>수임인(을): 법무법인 더 에이치 황해</div>
              </AgreementParties>

              <SectionContainer>
                <Section>
                  <SectionTitle>제1조(권한의 부여)</SectionTitle>
                  <SectionContent>
                    "갑"은 이 사건 소송대리사무를 처리하기 위하여 별첨
                    소송위임장에 기재된 권한을 "을"에게 부여한다.
                  </SectionContent>
                </Section>

                <Section>
                  <SectionTitle>제2조(착수비용 등)</SectionTitle>
                  <SectionContent>
                    "갑"은 이 사건의 착수비용으로 금 10,000원(￦10,000)을
                    "을"에게 지급한다(착수비용에는 인지대·송달료 등 소송진행에
                    필요한 비용이 포함되어 있으며 이 비용을 제외한 나머지는 을이
                    갑에게 지급하는 착수금 성격의 변호사보수로 간주함).
                    착수비용은 소송실비적 성격을 띠고 거래관행상 현저하게
                    염가임을 고려하여, "갑"은 본 계약 체결 이후 위임계약 해지 등
                    어떠한 사유로도 "을"에게 착수금의 반환을 청구할 수 없다.
                  </SectionContent>
                </Section>

                <Section>
                  <SectionTitle>제3조(추가비용)</SectionTitle>
                  <SectionContent>
                    제2조의 착수비용 외에 이 사건 위임사무를 처리하는 추가비용은
                    없는 것으로 한다. 다만, "갑"이 "을"과의 협의에 따르지 않고
                    제1심에 대한 불복항소, 상고심의 진행을 원하는 경우 인지대,
                    송달료를 부담하기로 한다.
                  </SectionContent>
                </Section>

                <Section>
                  <SectionTitle>제4조(성공보수금 등)</SectionTitle>
                  <SectionContent>
                    이 사건에서 승소(일부 승소·조정금·합의금·보상금 지급을
                    포함)할 경우, "갑"이 승소로 얻은 경제적
                    이익가액(판결원리금ㆍ조정금ㆍ합의금ㆍ보상금 포함, 이하 '이하
                    판결금 등'이라 함)을 기준으로 그 10%(부가가치세 별도)를
                    성공보수금으로 한다.
                    {"\n\n"}
                    '보상금'이라 함은, 쿠팡측의 손해배상책임과 관계없이
                    소송절차의 내외를 불문하고 쿠팡측이 일방적으로 정하거나
                    합의에 의하여 정해진, 보상 차원에서 "갑"에게 제공하는 일체의
                    경제적 이익을 가리킨다.
                    {"\n\n"}
                    "을"이 "갑"을 대신하여 판결금 등을 수령하는 경우, "을"은 7일
                    이내에 성공보수금을 공제한 잔액을 "갑"이 지정한 계좌에
                    입금하여 판결금 등의 정산을 진행하고, "갑"이 판결금 등을
                    수령하는 경우 "갑"은 7일 이내에 "을"이 지정하는 계좌에
                    성공보수금을 지급한다. 성공보수금의 지급이 지체될 경우
                    "갑"은 "을"에게 연 12%의 지연손해금을 지급한다.
                  </SectionContent>
                </Section>

                <Section>
                  <SectionTitle>제5조(승소로 보는 경우)</SectionTitle>
                  <SectionContent>
                    다음의 경우에는 승소한 것으로 보고 성공보수금을 지급하여야
                    한다.
                    {"\n"}① "갑"이 "을"의 승낙을 받지 않고 임의로 상대방과
                    합의를 한 경우
                    {"\n"}② "갑"이 "을"의 승낙 없이 임의로 소송을 취하하거나
                    인낙 또는 화해를 한 경우
                    {"\n"}③ "갑"이 이 약정에 정한 의무를 이행하지 않는 등 "갑"의
                    책임 있는 사유로 인해 "을"이 본 사건을 수행할 수 없어 사임한
                    경우
                    {"\n"}④ "갑"이 본 계약을 일방적으로 해제/해지하거나, "갑"의
                    귀책사유로 인해 계약이 해지된 경우
                  </SectionContent>
                </Section>

                <Section>
                  <SectionTitle>제6조(계약의 해제)</SectionTitle>
                  <SectionContent>
                    "갑"이 사건의 내용을 허위로 진술하거나 본 계약에서 정한
                    의무를 이행하지 않을 경우, "을"은 본 계약을 해지하고 소송
                    수행을 중단할 수 있다.
                  </SectionContent>
                </Section>

                <Section>
                  <SectionTitle>제7조(관할에 대한 합의)</SectionTitle>
                  <SectionContent>
                    본 계약과 관련한 일체의 분쟁에 대해서는 인천지방법원을
                    관할법원으로 한다.
                  </SectionContent>
                </Section>

                <Section>
                  <SectionTitle>제8조(인장의 조각 및 사용)</SectionTitle>
                  <SectionContent>
                    "갑"은 "을"이 본 계약에 따라 소송위임장, 소장 제출 및 판결금
                    등의 수령등을 위하여 필요할 경우 "갑"의 인장을 조각(전자적
                    방식을 포함한다)하여 사용하는 것에 동의한다.
                  </SectionContent>
                </Section>

                <Section>
                  <SectionTitle>제9조(개인정보 수집·이용 동의)</SectionTitle>
                  <SectionContent>
                    1. 수집 항목: 성명, 생년월일, 주민등록번호(13자리), 주소,
                    연락처, 계좌 정보(은행명, 계좌번호, 예금주명), 전자서명,
                    신분증 사본, 피해 내용 및 관련 증빙자료
                    {"\n"}2. 수집 목적: 소송 수행 및 관련 법률 서비스 제공,
                    의뢰인과의 연락 및 소송 진행 상황 안내, 판결금 및 보상금
                    정산
                    {"\n"}3. 보유 기간: 소송 종료 후 관련 법령에 따른 보존 기간
                    동안 보관 (통상 소송 종료 후 5년)
                    {"\n"}4. "갑"은 위와 같이 개인정보를 수집·이용하는 데
                    동의하며, "을"은 수집된 개인정보를 소송 목적 외에는 사용하지
                    않을 것을 확약한다. 다만 법원, 쿠팡 주식회사(피고), 소송
                    관련 전문가 및 유관 기관에 제공하는 것은 소송 수행을 위한
                    필요한 범위 내에서 허용된다.
                  </SectionContent>
                </Section>

                <SignatureDate>
                  {(() => {
                    const now = new Date();
                    return `${now.getFullYear()}년 ${
                      now.getMonth() + 1
                    }월 ${now.getDate()}일`;
                  })()}
                </SignatureDate>

                <SignatureSection>
                  <SignatureRow>
                    <SignatureLabel>"갑"</SignatureLabel>
                    <SignatureContent>
                      <SignatureLine>
                        성명 : {userData?.name || ""}
                      </SignatureLine>
                      <SignatureLine>
                        주소 : {userData?.address || ""},
                        {userData?.addressDetail || ""}
                      </SignatureLine>
                      <SignatureLine>
                        생년월일 :{" "}
                        {userData?.birthDate
                          ? (() => {
                              const bd = userData.birthDate
                                .toString()
                                .replace(/[^0-9]/g, "");
                              if (bd.length === 8) {
                                return `${bd.substring(0, 4)}년 ${bd.substring(
                                  4,
                                  6
                                )}월 ${bd.substring(6, 8)}일`;
                              }
                              return bd;
                            })()
                          : ""}
                      </SignatureLine>
                      <SignatureLine>
                        연락처 : {userData?.phoneNumber || ""}
                      </SignatureLine>
                      <SignatureLine>(인)</SignatureLine>
                    </SignatureContent>
                  </SignatureRow>

                  <SignatureRow>
                    <SignatureLabel>"을"</SignatureLabel>
                    <SignatureContent>
                      <SignatureLine>법무법인 더 에이치 황해</SignatureLine>
                      <SignatureLine>대표변호사 천 상 현</SignatureLine>
                      <SignatureLine>
                        인천광역시 미추홀구 학익소로 60, 제1층 (학익동,
                        반도빌딩)
                      </SignatureLine>
                    </SignatureContent>
                  </SignatureRow>
                </SignatureSection>

                <CheckboxContainer onClick={() => setAgreed(!agreed)}>
                  <CheckboxImage
                    src={
                      agreed ? "/png/checked.png" : "/png/checkedinActive.png"
                    }
                    alt="체크박스"
                  />
                  <CheckboxLabel>
                    위 내용을 모두 확인하고 동의합니다
                  </CheckboxLabel>
                </CheckboxContainer>
              </SectionContainer>
            </ModalBody>
            <ModalFooter>
              <ConfirmButton onClick={handleConfirm} disabled={!agreed}>
                동의하고 계속하기
              </ConfirmButton>
            </ModalFooter>
          </ModalContainer>
        )}
      </AnimatePresence>
    </ModalOverlay>
  );
};

// 스타일 컴포넌트
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  z-index: 999;
  touch-action: none;

  @media (min-width: 769px) {
    align-items: center;
  }
`;

const ModalContainer = styled(motion.div)`
  width: 100%;
  max-width: 500px;
  background-color: white;
  border-radius: 16px 16px 0 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: 80vh;

  @media (min-width: 769px) {
    border-radius: 16px;
    max-width: 420px;
    max-height: 90vh;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #eee;
`;

const ModalTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  flex: 1;
  text-align: center;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: #999;
  padding: 0;
  margin: 0;
  cursor: pointer;
`;

const ModalBody = styled.div`
  padding: 20px;
  overflow-y: auto;
  flex: 1;
  font-size: 14px;
  line-height: 1.6;
`;

const ModalFooter = styled.div`
  padding: 15px 20px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: center;
`;

const ConfirmButton = styled.button`
  display: flex;
  width: 100%;
  height: 56px;
  padding: 0 14px;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  background: ${(props) => (props.disabled ? "#E5E5E5" : "#0075ff")};
  color: ${(props) => (props.disabled ? "#999" : "white")};
  font-size: 16px;
  font-weight: 600;
  cursor: ${(props) => (props.disabled ? "default" : "pointer")};
  border: none;
  opacity: ${(props) => (props.disabled ? 0.8 : 1)};
  pointer-events: ${(props) => (props.disabled ? "none" : "auto")};
`;

const AgreementTitle = styled.div`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 20px;
  text-align: center;
`;

const AgreementParties = styled.div`
  margin-bottom: 20px;
  line-height: 1.6;

  div {
    margin-bottom: 8px;
  }
`;

const SectionContainer = styled.div`
  margin-bottom: 20px;
`;

const Section = styled.div`
  margin-bottom: 15px;
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #333;
`;

const SectionContent = styled.div`
  font-size: 14px;
  line-height: 1.6;
  color: #444;
  white-space: pre-line;
  margin-bottom: 15px;
`;

const SignatureDate = styled.div`
  margin: 20px 0;
  text-align: center;
  font-weight: 500;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: 15px;
  cursor: pointer;
`;

const CheckboxImage = styled.img`
  width: 24px;
  height: 24px;
  margin-right: 10px;
`;

const CheckboxLabel = styled.div`
  font-size: 15px;
  font-weight: 500;
`;

const SignatureSection = styled.div`
  margin-top: 30px;
  margin-bottom: 20px;
`;

const SignatureRow = styled.div`
  display: flex;
  margin-bottom: 20px;
  align-items: flex-start;
`;

const SignatureLabel = styled.div`
  font-weight: 600;
  margin-right: 20px;
  min-width: 40px;
`;

const SignatureContent = styled.div`
  flex: 1;
  line-height: 1.8;
`;

const SignatureLine = styled.div`
  margin-bottom: 4px;
  font-size: 14px;
`;

export default ElectronicSignature;
